// ── BarberVerse — Animations, Loader & Scroll Effects ──
import { HeroBgCanvas } from './effects/HeroBgCanvas.js';

// ─────────────────────────────────────────────────────
// CINEMATIC LOADER
// ─────────────────────────────────────────────────────
export function initCinematicLoader() {
    const loader = document.getElementById('cinematic-loader');
    const brand  = document.getElementById('loader-brand');
    const sub    = document.getElementById('loader-sub');
    const percent = document.querySelector('.loader-percent');
    const pole   = document.getElementById('loader-pole');
    const line   = document.getElementById('loader-line');

    if (!loader) return;

    // Phase 1 — pole grows
    if (pole) {
        gsap.to(pole, { height: 120, duration: 1, ease: 'expo.out', delay: 0.3 });
    }

    // Phase 2 — brand name
    if (brand) {
        gsap.to(brand, {
            opacity: 1, y: 0, duration: 1.2,
            ease: 'expo.out', delay: 0.8
        });
        gsap.set(brand, { y: 30 });
    }

    // Phase 3 — sub label
    if (sub) {
        gsap.set(sub, { opacity: 0 });
        gsap.to(sub, { opacity: 1, duration: 0.8, delay: 1.5 });
    }

    // Phase 4 — percent counter
    if (percent) {
        gsap.to(percent, { opacity: 1, duration: 0.5, delay: 1.8 });
    }

    // Animate the bottom progress line based on preloader num
    if (line) {
        const num = document.getElementById('preloader-num');
        let raf;
        function updateLine() {
            if (num) {
                const pct = parseInt(num.textContent, 10) || 0;
                line.style.width = pct + '%';
            }
            raf = requestAnimationFrame(updateLine);
        }
        raf = requestAnimationFrame(updateLine);
        // stop when loader hides
        loader.addEventListener('transitionend', () => cancelAnimationFrame(raf), { once: true });
    }
}

export function hideCinematicLoader(onDone) {
    const loader = document.getElementById('cinematic-loader');
    if (!loader) { if (onDone) onDone(); return; }

    // Force 100% on line
    const line = document.getElementById('loader-line');
    if (line) line.style.width = '100%';

    // Short pause then fade out
    setTimeout(() => {
        loader.style.opacity = '0';
        setTimeout(() => {
            loader.style.display = 'none';
            document.body.classList.remove('is-loading');
            if (onDone) onDone();
        }, 1200);
    }, 400);
}

// ─────────────────────────────────────────────────────
// HERO ANIMATIONS (runs after loader hides)
// ─────────────────────────────────────────────────────
export function initHeroAnimations() {
    if (!window.gsap) return;

    const tl = gsap.timeline({ defaults: { ease: 'expo.out' } });

    // Eyebrow
    tl.to('#hero-eyebrow', {
        opacity: 1, y: 0, duration: 1, visibility: 'visible'
    }, 0);

    // Hero title lines — slide up from clipped position
    tl.fromTo('#hero-line-1', { y: '105%' }, { y: '0%', duration: 1.4 }, 0.1)
      .fromTo('#hero-line-2', { y: '105%' }, { y: '0%', duration: 1.4 }, 0.22)
      .fromTo('#hero-line-3', { y: '105%' }, { y: '0%', duration: 1.4 }, 0.34);

    // Gold line-3 color reveal
    tl.fromTo('#hero-line-3 .char-wrap',
        { color: 'rgba(240,235,225,0)' },
        { color: 'var(--gold)', stagger: 0.08, duration: 0.8, ease: 'power2.out' },
        0.8
    );

    // Sub paragraph
    tl.to('#hero-sub', {
        opacity: 1, y: 0, duration: 1, visibility: 'visible'
    }, 0.6);

    // CTA buttons
    tl.to('#hero-actions', {
        opacity: 1, y: 0, duration: 0.9, visibility: 'visible'
    }, 0.75);

    // Scroll hint
    tl.to('#scroll-hint', {
        opacity: 1, y: 0, duration: 0.8, visibility: 'visible'
    }, 1.0);

    // Stats
    tl.to('#hero-stats', {
        opacity: 1, y: 0, duration: 0.8, visibility: 'visible'
    }, 1.1);

    // Header fade in
    tl.fromTo('#site-header',
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.8 },
        0.2
    );
}

// ─────────────────────────────────────────────────────
// SCROLL ANIMATIONS
// ─────────────────────────────────────────────────────
export function initScrollAnimations() {
    if (!window.gsap || !window.ScrollTrigger) return;
    gsap.registerPlugin(ScrollTrigger);

    // Generic scroll reveals
    const scrollEls = document.querySelectorAll('.gsap-scroll');
    scrollEls.forEach((el) => {
        gsap.fromTo(el,
            { y: 40, opacity: 0, visibility: 'hidden' },
            {
                scrollTrigger: {
                    trigger: el,
                    start: 'top 88%',
                    once: true,
                },
                y: 0,
                opacity: 1,
                visibility: 'visible',
                duration: 1.1,
                ease: 'expo.out'
            }
        );
    });

    // Craft divider line
    const divider = document.querySelector('.craft-divider');
    if (divider) {
        ScrollTrigger.create({
            trigger: divider,
            start: 'top 85%',
            onEnter: () => divider.classList.add('in-view'),
        });
    }

    // About ghost text parallax
    const ghostText = document.querySelector('.about-accent-text');
    if (ghostText) {
        gsap.fromTo(ghostText,
            { x: '-8%' },
            {
                x: '3%',
                ease: 'none',
                scrollTrigger: {
                    trigger: '#about',
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: 1.5,
                }
            }
        );
    }

    // Header scroll state
    ScrollTrigger.create({
        start: 100,
        onEnter:  () => document.getElementById('site-header')?.classList.add('scrolled'),
        onLeaveBack: () => document.getElementById('site-header')?.classList.remove('scrolled'),
    });

    // Stat counters (hero)
    document.querySelectorAll('[data-count]').forEach((el) => {
        const target  = parseInt(el.dataset.count, 10);
        const suffix  = el.dataset.suffix || '';
        ScrollTrigger.create({
            trigger: el,
            start: 'top 90%',
            once: true,
            onEnter: () => {
                const obj = { val: 0 };
                gsap.to(obj, {
                    val: target,
                    duration: 1.5,
                    ease: 'power2.out',
                    onUpdate: () => { el.textContent = Math.floor(obj.val) + suffix; }
                });
            }
        });
    });

    // Fashion cards stagger
    const cards = document.querySelectorAll('.fashion-card');
    cards.forEach((card, i) => {
        gsap.fromTo(card,
            { opacity: 0, y: 60, scale: 0.97 },
            {
                scrollTrigger: {
                    trigger: card,
                    start: 'top 90%',
                    once: true,
                },
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 1.0,
                delay: i * 0.12,
                ease: 'expo.out',
                visibility: 'visible',
                clearProps: 'visibility'
            }
        );
    });
}

// ─────────────────────────────────────────────────────
// HOLOGRAPHIC SCAN OVERLAY
// ─────────────────────────────────────────────────────
export function triggerHolographicScan(onComplete) {
    const overlay  = document.getElementById('hologram-overlay');
    const scanLine = overlay?.querySelector('.scan-line-horizontal');
    const scanText = overlay?.querySelector('.scan-text');

    if (!overlay || !scanLine || !scanText) {
        if (onComplete) onComplete();
        return;
    }

    overlay.style.display = 'flex';

    if (window.gsap) {
        const tl = gsap.timeline({
            onComplete: () => {
                gsap.to(overlay, {
                    opacity: 0, duration: 0.5,
                    onComplete: () => {
                        overlay.style.display = 'none';
                        overlay.style.opacity = '1';
                        if (onComplete) onComplete();
                    }
                });
            }
        });
        tl.to(scanLine, { opacity: 1, duration: 0.2 })
          .to(scanLine, { top: '100%', duration: 1.5, ease: 'linear' })
          .to(scanText, { opacity: 1, duration: 0.5 }, '-=1.5')
          .to(scanLine, { opacity: 0, duration: 0.2 });
    } else {
        setTimeout(() => {
            overlay.style.display = 'none';
            if (onComplete) onComplete();
        }, 2000);
    }
}

// ─────────────────────────────────────────────────────
// AMBIENT DOM PARTICLES
// ─────────────────────────────────────────────────────
export function initDOMParticles() {
    const container = document.getElementById('ambient-particles');
    if (!container) return;

    for (let i = 0; i < 30; i++) {
        const p = document.createElement('div');
        p.classList.add('particle');
        const size = 1 + Math.random() * 2.5;
        p.style.width  = `${size}px`;
        p.style.height = `${size}px`;
        p.style.left   = `${Math.random() * 100}vw`;
        p.style.top    = `${Math.random() * 100}vh`;
        p.style.animationDuration = `${12 + Math.random() * 14}s`;
        p.style.animationDelay    = `${Math.random() * 10}s`;
        container.appendChild(p);
    }
}

// ─────────────────────────────────────────────────────
// MOBILE NAV
// ─────────────────────────────────────────────────────
export function initMobileNav() {
    const toggle  = document.getElementById('nav-toggle');
    const overlay = document.getElementById('mobile-nav');
    if (!toggle || !overlay) return;

    toggle.addEventListener('click', () => {
        const isOpen = toggle.classList.toggle('open');
        overlay.classList.toggle('open', isOpen);
        toggle.setAttribute('aria-expanded', isOpen);
    });

    // Close on link click
    overlay.querySelectorAll('a').forEach((a) => {
        a.addEventListener('click', () => {
            toggle.classList.remove('open');
            overlay.classList.remove('open');
            toggle.setAttribute('aria-expanded', false);
        });
    });
}

// ─────────────────────────────────────────────────────
// HERO BG CANVAS
// ─────────────────────────────────────────────────────
export function initHeroBgCanvas() {
    return new HeroBgCanvas('hero-bg-canvas');
}

// ─────────────────────────────────────────────────────
// MAGNETIC BUTTONS (enhanced)
// ─────────────────────────────────────────────────────
export function initMagneticButtons() {
    document.querySelectorAll('.magnetic').forEach((el) => {
        el.addEventListener('mousemove', (e) => {
            const r  = el.getBoundingClientRect();
            const mx = e.clientX - (r.left + r.width / 2);
            const my = e.clientY - (r.top  + r.height / 2);
            el.style.setProperty('--mx', (mx * 0.3).toFixed(1));
            el.style.setProperty('--my', (my * 0.3).toFixed(1));
        });
        el.addEventListener('mouseleave', () => {
            el.style.setProperty('--mx', 0);
            el.style.setProperty('--my', 0);
        });
    });
}
